import React, { useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import TierSection, { TierVariant } from '../dashboard/TierSection'
import { ThemeAICodeFrame, ThemeAICodeFrameListResponse } from '../../types/gotypes'

interface CodeFramePickerDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (frame: ThemeAICodeFrame) => void
  tiers: ThemeAICodeFrameListResponse
  loading: boolean
  /** Managers may move a frame to or from the company level. */
  canPromote?: boolean
  onPromote?: (frame: ThemeAICodeFrame) => void
  onDemote?: (frame: ThemeAICodeFrame) => void
}

interface PickerTier {
  variant: TierVariant
  title: string
  emptyLabel: string
  frames: ThemeAICodeFrame[]
}

// The code frame library, in the same three tiers as Data Viz and drawn with
// the same TierSection (#1492 item 3; Matt, 23 Sep: "We should have
// organizational-level code frames ... exactly the same style as Data Viz").
// Data Viz calls that tier "Company-level", so this does too.
const CodeFramePickerDialog: React.FC<CodeFramePickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  tiers,
  loading,
  canPromote = false,
  onPromote,
  onDemote,
}) => {
  const [search, setSearch] = useState('')
  const [openTiers, setOpenTiers] = useState<Partial<Record<TierVariant, boolean>>>({})

  const query = search.trim().toLowerCase()
  const filterTier = (frames: ThemeAICodeFrame[]) => {
    const visible = frames.filter((frame) => !frame.archived)
    if (!query) return visible
    return visible.filter((frame) => frame.name.toLowerCase().includes(query) || frame.owner.toLowerCase().includes(query))
  }

  const pickerTiers: PickerTier[] = [
    { variant: 'company', title: 'Company-level', emptyLabel: 'No company-level code frames yet', frames: filterTier(tiers.exec) },
    { variant: 'mine', title: 'My code frames', emptyLabel: "You haven't created any code frames for this job yet", frames: filterTier(tiers.mine) },
    { variant: 'colleagues', title: "My colleagues' code frames", emptyLabel: 'No colleagues have code frames in this job yet', frames: filterTier(tiers.colleagues) },
  ]

  // As in Data Viz, a tier starts open when it has frames and closed when it
  // is empty, until the user toggles it.
  const isTierOpen = (tier: PickerTier) => openTiers[tier.variant] ?? tier.frames.length > 0
  const toggleTier = (tier: PickerTier) => setOpenTiers({ ...openTiers, [tier.variant]: !isTierOpen(tier) })

  const handleSelect = (frame: ThemeAICodeFrame) => {
    onSelect(frame)
    setSearch('')
    onClose()
  }

  const renderRow = (frame: ThemeAICodeFrame) => {
    const isCompany = frame.visibility === 'exec'
    const showStar = canPromote && !frame.standard
    return (
      <ListItem
        key={frame.id}
        disablePadding
        secondaryAction={showStar ? (
          <Tooltip title={isCompany ? 'Demote from company-level' : 'Promote to company-level'}>
            <IconButton
              edge="end"
              size="small"
              onClick={() => (isCompany ? onDemote?.(frame) : onPromote?.(frame))}
              aria-label={isCompany ? `Demote ${frame.name} from company-level` : `Promote ${frame.name} to company-level`}
            >
              {isCompany ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : undefined}
        sx={{ backgroundColor: 'background.paper', borderRadius: 1, mb: 0.5 }}
      >
        <ListItemButton onClick={() => handleSelect(frame)} sx={{ borderRadius: 1 }}>
          <ListItemText
            primary={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {frame.name}
                {frame.standard && <Chip label="Standard" size="small" variant="outlined" />}
              </Box>
            }
            secondary={frame.standard ? 'The same for every client' : `v${frame.latest_version || 1} · ${frame.owner}`}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Choose a code frame</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search code frames…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          sx={{ mb: 2, mt: 1 }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 480, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pickerTiers.map((tier) => (
              <TierSection
                key={tier.variant}
                title={tier.title}
                count={tier.frames.length}
                open={isTierOpen(tier)}
                onToggle={() => toggleTier(tier)}
                variant={tier.variant}
              >
                {tier.frames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, px: 1 }}>
                    {query ? 'No matches' : tier.emptyLabel}
                  </Typography>
                ) : (
                  <List disablePadding>{tier.frames.map(renderRow)}</List>
                )}
              </TierSection>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CodeFramePickerDialog
